import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { HeroUserPillComponent } from './hero-user-pill.component';
import { PortalNavbarComponent } from './portal-navbar.component';
import { AuthService } from '../services/auth.service';
import { EventService, EventDto } from '../services/event.service';
import { OrganizationService } from '../services/organization.service';
import { QuestionService, QuestionDto, QuestionCreateDto, QuestionUpdateDto, GameFlowModeDto } from '../services/question.service';
import { QuestionAssignmentService, QuestionAssignmentDto, QuestionAssignmentCreateDto } from '../services/question-assignment.service';
import { AnswerService, AnswerDto, AnswerUpdateDto } from '../services/answer.service';
import { AnswerHintService, AnswerHintDto } from '../services/answer-hint.service';

interface QuestionRecord {
  id: number;
  organizationId: number;
  gameFlowId: number;
  text: string;
  isEnabled: boolean;
  isDeleted: boolean;
}

interface QuestionDraft {
  text: string;
  gameFlowId: number | null;
}

interface EventOption {
  id: number;
  name: string;
}

interface AnswerRecord {
  id: number;
  questionId: number;
  text: string;
  order: number;
  points: number;
  hintId: number | null;
  hintChecked: boolean;
  hintText: string;
}

interface QuestionAssignment {
  id: number;
  questionId: number;
  eventId: number;
  round: number;
}

const PENALTY_SHOT_MODE_ID = 103;
const HEAD_TO_HEAD_MODE_ID = 104;

type QuestionActionType = 'enable' | 'disable' | 'softDelete' | 'restore' | 'hardDelete';
type QuestionLifecycleAction = 'none' | 'disable' | 'restore' | 'delete';

@Component({
  selector: 'app-questions-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule, PortalNavbarComponent, HeroUserPillComponent],
  template: `
    <div class="page">
      <app-portal-navbar></app-portal-navbar>

      <!-- ══════════════════ HERO ══════════════════════════════ -->
      <section class="page-hero">
        <div class="page-hero-inner">
          <a routerLink="/" class="home-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                 viewBox="0 0 24 24" fill="none" stroke="currentColor"
                 stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
            Home
          </a>
          <div class="hero-top-row">
            <div class="hero-icon hero-icon--indigo">
              <svg xmlns="http://www.w3.org/2000/svg" width="26" height="26"
                   viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="12" cy="12" r="10"/>
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </div>
            <app-hero-user-pill></app-hero-user-pill>
          </div>
          <h1 class="page-title">Questions</h1>
          <p class="page-sub">Build and curate your trivia question catalog.</p>
        </div>
      </section>

      <!-- ══════════════════ BODY ═══════════════════════════════ -->
      <section class="page-body">
        <div class="page-body-inner">

          <!-- ── Action bar ──────────────────────────────────── -->
          <div class="action-bar">
            <button class="btn btn--primary" (click)="openNew()">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                   viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5"  y1="12" x2="19" y2="12"/>
              </svg>
              New Question
            </button>
          </div>

          <!-- Questions list -->
          <div class="questions-card">
            <div class="questions-card-hd">
              <span class="questions-card-title">All Questions</span>
              <div class="questions-hd-right">
                <div class="questions-filter-wrap">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"
                       viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <circle cx="11" cy="11" r="8"/>
                    <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <input class="questions-filter-input" type="text"
                         [(ngModel)]="filterText"
                         placeholder="Filter questions…">
                </div>
                <div class="filter-wrap">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"
                       viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                  </svg>
                  <select class="filter-select" [(ngModel)]="questionFilterEventId">
                    <option [ngValue]="0">All Events</option>
                    @for (evt of eventOptions; track evt.id) {
                      <option [ngValue]="evt.id">{{ evt.name }}</option>
                    }
                  </select>
                </div>
                <span class="questions-count">
                  {{ filteredQuestions.length }} question{{ filteredQuestions.length !== 1 ? 's' : '' }}
                </span>
              </div>
            </div>

            <div class="questions-col-hd">
              <span class="col-question">Question</span>
              <span class="col-game-flow">Game Flow</span>
              <span class="col-event">Event</span>
              <span class="col-status">Status</span>
              <span class="col-del"></span>
            </div>

            <div class="questions-list">
              @for (q of filteredQuestions; track q.id) {
                <div class="question-row"
                     [class.row--selected]="selectedQuestion?.id === q.id"
                     (click)="selectQuestion(q)"
                     (dblclick)="openQuestionDetails(q)">
                  <span class="question-text">{{ q.text }}</span>

                  <span class="question-game-flow" [title]="getGameFlowModeName(q.gameFlowId)">
                    @if (getGameFlowModeName(q.gameFlowId) === '3/3/And-Out') {
                      <svg class="question-game-flow-icon" xmlns="http://w3.org" viewBox="0 0 75 75" width="75" height="75" aria-label="3/3/And-Out" role="img">
                        <rect width="75" height="75" rx="8" fill="#111827"/>
                        <text x="37.5" y="34"
                              font-family="system-ui, -apple-system, sans-serif"
                              font-size="22"
                              font-weight="900"
                              fill="#F9FAFB"
                              text-anchor="middle"
                              letter-spacing="0.5">3/3</text>
                        <text x="37.5" y="58"
                              font-family="system-ui, -apple-system, sans-serif"
                              font-size="18"
                              font-weight="900"
                              fill="#EF4444"
                              text-anchor="middle"
                              letter-spacing="1">OUT</text>
                      </svg>
                    } @else if (getGameFlowModeName(q.gameFlowId) === '3/3/Sudden-Death-Hint') {
                      <svg class="question-game-flow-icon" xmlns="http://w3.org" viewBox="0 0 100 100" width="100" height="100" aria-label="3/3/Sudden-Death-Hint" role="img">
                        <rect width="100" height="100" rx="12" fill="#111827"/>
                        <text x="50" y="28"
                              font-family="system-ui, -apple-system, sans-serif"
                              font-size="20"
                              font-weight="900"
                              fill="#F9FAFB"
                              text-anchor="middle"
                              letter-spacing="0.5">3/3</text>
                        <g transform="translate(50, 62) scale(1.4)" text-anchor="middle">
                          <path d="M-15,-15 L15,15 M-15,15 L15,-15" stroke="#EF4444" stroke-width="3.5" stroke-linecap="round"/>
                          <circle cx="-15" cy="-15" r="2" fill="#EF4444"/>
                          <circle cx="15" cy="15" r="2" fill="#EF4444"/>
                          <circle cx="-15" cy="15" r="2" fill="#EF4444"/>
                          <circle cx="15" cy="-15" r="2" fill="#EF4444"/>
                          <path d="M-8,-6 C-8,-13 8,-13 8,-6 C8,-2 6,0 4,4 L-4,4 C-6,0 -8,-2 -8,-6 Z" fill="#EF4444"/>
                          <path d="M-2,4 L-2,1 M0,4 L0,1 M2,4 L2,1" stroke="#111827" stroke-width="1"/>
                          <circle cx="-3.5" cy="-6" r="2" fill="#111827"/>
                          <circle cx="3.5" cy="-6" r="2" fill="#111827"/>
                          <polygon points="0,-3 -1.5,-1 1.5,-1" fill="#111827"/>
                        </g>
                      </svg>
                    } @else if (getGameFlowModeName(q.gameFlowId) === 'HeadtoHead') {
                      <svg class="question-game-flow-icon" xmlns="http://w3.org" viewBox="0 0 100 100" width="100" height="100" aria-label="HeadtoHead" role="img">
                        <rect width="100" height="100" rx="12" fill="#111827"/>
                        <text x="50" y="28"
                              font-family="system-ui, -apple-system, sans-serif"
                              font-size="20"
                              font-weight="900"
                              fill="#F9FAFB"
                              text-anchor="middle"
                              letter-spacing="0.5">3/3</text>
                        <g transform="translate(50, 65)">
                          <g transform="rotate(45)">
                            <path d="M 0,-24 L 3,-21 L 2,4 L -2,4 L -3,-21 Z" fill="#38BDF8"/>
                            <path d="M -6,4 L 6,4 L 6,7 L -6,7 Z" fill="#E2E8F0"/>
                            <path d="M -2,7 L 2,7 L 2,13 L -2,13 Z" fill="#64748B"/>
                          </g>
                          <g transform="rotate(-45)">
                            <path d="M 0,-24 L 3,-21 L 2,4 L -2,4 L -3,-21 Z" fill="#F97316"/>
                            <path d="M -6,4 L 6,4 L 6,7 L -6,7 Z" fill="#E2E8F0"/>
                            <path d="M -2,7 L 2,7 L 2,13 L -2,13 Z" fill="#64748B"/>
                          </g>
                          <circle cx="0" cy="-10" r="3" fill="#FFFFFF"/>
                        </g>
                      </svg>
                    } @else if (getGameFlowModeName(q.gameFlowId) === 'PenalityShot' || getGameFlowModeName(q.gameFlowId) === 'PenaltyShot') {
                      <svg class="question-game-flow-icon" xmlns="http://w3.org" viewBox="0 0 100 100" width="100" height="100" aria-label="PenalityShot" role="img">
                        <rect width="100" height="100" rx="12" fill="#111827"/>
                        <text x="50" y="28"
                              font-family="system-ui, -apple-system, sans-serif"
                              font-size="20"
                              font-weight="900"
                              fill="#F9FAFB"
                              text-anchor="middle"
                              letter-spacing="0.5">3/3</text>
                        <g transform="translate(50, 62)">
                          <circle cx="0" cy="0" r="22" fill="none" stroke="#EF4444" stroke-width="4"/>
                          <circle cx="0" cy="0" r="13" fill="none" stroke="#F9FAFB" stroke-width="3"/>
                          <circle cx="0" cy="0" r="5" fill="#EF4444"/>
                          <path d="M -26,0 L -18,0 M 18,0 L 26,0 M 0,-26 L 0,-18 M 0,18 L 0,26" stroke="#EF4444" stroke-width="3" stroke-linecap="round"/>
                        </g>
                      </svg>
                    } @else {
                      {{ getGameFlowModeName(q.gameFlowId) }}
                    }
                  </span>

                  <span class="question-event">{{ getQuestionEventNames(q.id) }}</span>

                  <span class="question-status" [class.question-status--disabled]="!q.isEnabled">
                    {{ q.isEnabled ? 'Enabled' : 'Disabled' }}
                  </span>

                  <button class="row-chevron"
                          (click)="openQuestionDetails(q); $event.stopPropagation()"
                          title="Edit question">
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13"
                         viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <polyline points="9 18 15 12 9 6"/>
                    </svg>
                  </button>
                </div>
              } @empty {
                <div class="questions-empty">
                  No questions yet — click New Question to add one.
                </div>
              }
            </div>
          </div>

          <!-- ── Action bar ──────────────────────────────────── -->
          <div class="action-bar">
            <button class="btn btn--primary"
                    [disabled]="!canOpenAssignmentModal"
                    (click)="openAssignmentModal()">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14"
                   viewBox="0 0 24 24" fill="none" stroke="currentColor"
                   stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                <line x1="12" y1="5" x2="12" y2="19"/>
                <line x1="5"  y1="12" x2="19" y2="12"/>
              </svg>
              New Assignment
            </button>
          </div>

          <!-- ── Answers section ─────────────────────────────── -->
          @if (selectedQuestion) {
            <div class="answers-card">
              <div class="answers-card-hd">
                <span class="answers-card-title">Answers</span>
                <span class="answers-sub">{{ selectedQuestion.text }}</span>
              </div>
              <div class="answers-grid">
                @for (ans of activeAnswers; track ans.id) {
                  <div class="answer-item"
                       draggable="true"
                       [class.is-dragging]="dragSourceId === ans.id"
                       [class.is-drag-over]="dragOverId === ans.id"
                       (dragstart)="onDragStart(ans, $event)"
                       (dragover)="onDragOver(ans, $event)"
                       (dragleave)="onDragLeave($event)"
                       (drop)="onDrop(ans, $event)"
                       (dragend)="onDragEnd()">
                    <span class="answer-drag-handle" title="Drag to reorder">
                      <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11"
                           viewBox="0 0 24 24" fill="currentColor">
                        <circle cx="9"  cy="5"  r="1.8"/>
                        <circle cx="15" cy="5"  r="1.8"/>
                        <circle cx="9"  cy="12" r="1.8"/>
                        <circle cx="15" cy="12" r="1.8"/>
                        <circle cx="9"  cy="19" r="1.8"/>
                        <circle cx="15" cy="19" r="1.8"/>
                      </svg>
                    </span>
                    <span class="answer-num">{{ ans.order }}</span>
                    <div class="answer-main-wrap">
                      <input class="answer-input" type="text"
                             [(ngModel)]="ans.text"
                             placeholder=" "
                             (ngModelChange)="onAnswerTextChanged(ans, $event)"
                             (blur)="saveAnswer(ans)"
                             (dragstart)="$event.stopPropagation()">
                      @if (selectedQuestionSupportsHints && hasAnswerText(ans)) {
                        <div class="answer-hint-wrap"
                             title="Hints are optional and can be filled out at any time.">
                          <label class="answer-hint-toggle">
                            <input type="checkbox"
                                   [ngModel]="ans.hintChecked"
                                   (ngModelChange)="onAnswerHintCheckedChanged(ans, $event)"
                                   (dragstart)="$event.stopPropagation()">
                            <span>{{ ans.hintChecked ? 'Hint' : 'Add Hint' }}</span>
                          </label>
                          @if (ans.hintChecked) {
                            <textarea class="answer-hint-input"
                                      [(ngModel)]="ans.hintText"
                                      maxlength="500"
                                      placeholder="Enter a hint for this answer..."
                                      (ngModelChange)="onAnswerHintChanged(ans, $event)"
                                      (keydown.enter)="onAnswerHintEnter(ans, $event)"
                                      (blur)="saveAnswerHint(ans)"
                                      (dragstart)="$event.stopPropagation()"></textarea>
                          }
                        </div>
                      }
                    </div>
                    @if (hasAnswerText(ans)) {
                      <div class="answer-points-wrap">
                        <input class="answer-points-input" type="number"
                               [(ngModel)]="ans.points"
                               min="0"
                               max="9999"
                               step="1"
                               aria-label="Answer points"
                               (ngModelChange)="onAnswerPointsChanged(ans, $event)"
                               (blur)="saveAnswer(ans)"
                               (dragstart)="$event.stopPropagation()">
                        @if (getAnswerPointsValidationMessage(ans.id)) {
                          <span class="answer-points-validation">{{ getAnswerPointsValidationMessage(ans.id) }}</span>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }

          <!-- ── Question Event Assignments ─────────────────── -->
          <div class="assignments-card">
            <div class="assignments-card-hd">
              <span class="assignments-card-title">Event Assignments</span>
              <div class="assignments-hd-right">
                <div class="filter-wrap">
                  <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12"
                       viewBox="0 0 24 24" fill="none" stroke="currentColor"
                       stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                  </svg>
                  <select class="filter-select" [(ngModel)]="filterEventId">
                    <option [ngValue]="0">All Events</option>
                    @for (evt of eventOptions; track evt.id) {
                      <option [ngValue]="evt.id">{{ evt.name }}</option>
                    }
                  </select>
                </div>
                <span class="assignments-count">
                  {{ filteredAssignments.length }} assignment{{ filteredAssignments.length !== 1 ? 's' : '' }}
                </span>
              </div>
            </div>

            <div class="assignments-col-hd">
              <span class="col-drag"></span>
              <span class="col-question">Question</span>
              <span class="col-event">Event</span>
              <span class="col-round">Round</span>
              <span class="col-del"></span>
            </div>

            <div class="assignments-list">
              @for (asgn of filteredAssignments; track asgn.id) {
                <div class="assignment-row"
                     draggable="true"
                     [class.is-dragging]="assignmentDragSourceId === asgn.id"
                     [class.is-drag-over]="assignmentDragOverId === asgn.id"
                     [class.is-saving]="isAssignmentReorderSaving"
                     (dragstart)="onAssignmentDragStart(asgn, $event)"
                     (dragover)="onAssignmentDragOver(asgn, $event)"
                     (dragleave)="onAssignmentDragLeave($event)"
                     (drop)="onAssignmentDrop(asgn, $event)"
                     (dragend)="onAssignmentDragEnd()">
                  <span class="assignment-drag-handle" title="Drag to reorder">
                    <svg xmlns="http://www.w3.org/2000/svg" width="11" height="11"
                         viewBox="0 0 24 24" fill="currentColor">
                      <circle cx="9"  cy="5"  r="1.8"/>
                      <circle cx="15" cy="5"  r="1.8"/>
                      <circle cx="9"  cy="12" r="1.8"/>
                      <circle cx="15" cy="12" r="1.8"/>
                      <circle cx="9"  cy="19" r="1.8"/>
                      <circle cx="15" cy="19" r="1.8"/>
                    </svg>
                  </span>
                  <span class="asgn-readonly">{{ getQuestionText(asgn.questionId) }}</span>
                  <span class="asgn-readonly">{{ getEventName(asgn.eventId) }}</span>
                  <span class="asgn-readonly asgn-readonly--round">{{ asgn.round }}</span>
                  <button class="asgn-delete" (click)="deleteAssignment(asgn.id)" title="Remove assignment">
                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13"
                         viewBox="0 0 24 24" fill="none" stroke="currentColor"
                         stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                      <line x1="18" y1="6" x2="6" y2="18"/>
                      <line x1="6"  y1="6" x2="18" y2="18"/>
                    </svg>
                  </button>
                </div>
              } @empty {
                <div class="assignments-empty">
                  No assignments yet — click New Assignment to place a question in an event.
                </div>
              }
            </div>
          </div>

        </div>
      </section>

      @if (isQuestionDetailModalOpen && editingQuestion) {
        <div class="modal-backdrop" (click)="closeQuestionDetailsModal()">
          <div class="modal" (click)="$event.stopPropagation()">
            <div class="modal-hd">
              <h2 class="modal-title">Question Details</h2>
              <button class="modal-close" (click)="closeQuestionDetailsModal()" aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6"  x2="6"  y2="18"/>
                  <line x1="6"  y1="6"  x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div class="modal-body">
              <div class="modal-section">
                <div class="field">
                  <label class="field-label" for="question-detail-text">Question</label>
                  <textarea id="question-detail-text" class="field-textarea"
                            [(ngModel)]="editingQuestion.text"
                            spellcheck="true"
                            rows="3"></textarea>
                </div>
                <div class="field">
                  <label class="field-label" for="question-detail-gameflow">Game Flow</label>
                  <select id="question-detail-gameflow" class="field-select" [(ngModel)]="editingQuestion.gameFlowId">
                    @for (mode of gameFlowModes; track mode.id) {
                      <option [ngValue]="mode.id">{{ mode.name }}</option>
                    }
                  </select>
                </div>
                <div class="gameflow-summary">
                  <p>101. 3/3/And-Out - Multiple Answers.</p>
                  <p>102. 3/3/Sudden-Death-Hint - Multiple Answers.</p>
                  <p>104. Head to Head - 1 Answer.</p>
                  <p>103. Penalty Shot - 1 Answer.</p>
                </div>
              </div>

            </div>

            <div class="modal-ft modal-ft--question-details">
              <div class="modal-ft-row">
                <div class="modal-actions">
                  @if (canManageQuestionLifecycle && !editingQuestion.isDeleted) {
                    @if (editingQuestion.isEnabled) {
                      <button
                        class="btn btn--lifecycle-blue"
                        [class.btn--lifecycle-blue-active]="isQuestionLifecycleActionSelected('disable')"
                        [disabled]="editingQuestionHasAssignments"
                        (click)="selectQuestionLifecycleAction('disable')">
                        Disable
                      </button>
                    } @else {
                      <button
                        class="btn btn--lifecycle-blue"
                        [class.btn--lifecycle-blue-active]="isQuestionLifecycleActionSelected('restore')"
                        (click)="selectQuestionLifecycleAction('restore')">
                        Restore
                      </button>
                    }
                    <button
                      class="btn btn--danger"
                      [class.btn--danger-active]="isQuestionLifecycleActionSelected('delete')"
                      [disabled]="editingQuestionHasAssignments"
                      (click)="selectQuestionLifecycleAction('delete')">
                      Delete
                    </button>
                  }
                  @if (canRestoreDeletedQuestions && editingQuestion.isDeleted) {
                    <button
                      class="btn btn--lifecycle-blue"
                      [class.btn--lifecycle-blue-active]="isQuestionLifecycleActionSelected('restore')"
                      (click)="selectQuestionLifecycleAction('restore')">
                      Restore
                    </button>
                  }
                </div>
                <div class="modal-actions">
                  <button class="btn btn--ghost" (click)="closeQuestionDetailsModal()">Cancel</button>
                  <button class="btn btn--primary" [disabled]="editingQuestion.isDeleted" (click)="saveQuestionDetails()">OK</button>
                </div>
              </div>

              @if (questionDetailToastMessage) {
                <div class="modal-toast modal-toast--error" role="alert" aria-live="assertive">
                  <span class="modal-toast-title">Unable to save</span>
                  <span>{{ questionDetailToastMessage }}</span>
                </div>
              }
            </div>
          </div>
        </div>
      }

      <!-- ══════════════════ MODAL ══════════════════════════════ -->
      @if (isNewQuestionModalOpen) {
        <div class="modal-backdrop" (click)="closeNewQuestionModal()">
          <div class="modal" (click)="$event.stopPropagation()">

            <!-- Header -->
            <div class="modal-hd">
              <h2 class="modal-title">New Question</h2>
              <button class="modal-close" (click)="closeNewQuestionModal()" aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6"  x2="6"  y2="18"/>
                  <line x1="6"  y1="6"  x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <!-- Fields -->
            <div class="modal-body">
              <div class="field">
                <label class="field-label" for="q-text">Question</label>
                <textarea id="q-text" class="field-textarea"
                          [(ngModel)]="draft.text"
                          spellcheck="true"
                          placeholder="Enter your question…"
                          rows="3"></textarea>
              </div>
              <div class="field">
                <label class="field-label" for="q-gameflow">Game Flow</label>
                <select id="q-gameflow" class="field-select" [(ngModel)]="draft.gameFlowId">
                  @for (mode of gameFlowModes; track mode.id) {
                    <option [ngValue]="mode.id">{{ mode.name }}</option>
                  }
                </select>
              </div>
            </div>

            <!-- Footer -->
            <div class="modal-ft modal-ft--end">
              <div class="modal-actions">
                <button class="btn btn--ghost" (click)="closeNewQuestionModal()">Cancel</button>
                <button class="btn btn--primary" (click)="saveQuestion()">OK</button>
              </div>
            </div>

          </div>
        </div>
      }

      @if (isAssignmentModalOpen) {
        <div class="modal-backdrop" (click)="closeAssignmentModal()">
          <div class="modal" (click)="$event.stopPropagation()">
            <div class="modal-hd">
              <h2 class="modal-title">New Assignment</h2>
              <button class="modal-close" (click)="closeAssignmentModal()" aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6"  x2="6"  y2="18"/>
                  <line x1="6"  y1="6"  x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div class="modal-body">
              <div class="field">
                <label class="field-label">Question</label>
                <input class="field-input" type="text" [ngModel]="selectedQuestion?.text ?? ''" readonly>
              </div>

              <div class="field">
                <label class="field-label" for="qa-events">Assigned Events</label>
                <select id="qa-events"
                        class="field-select"
                        multiple
                        [(ngModel)]="assignmentDraft.eventIds"
                        (ngModelChange)="onAssignmentEventSelectionChanged($event)">
                  @for (evt of eventOptions; track evt.id) {
                    <option [ngValue]="evt.id">{{ evt.name }}</option>
                  }
                </select>
              </div>

              <div class="field">
                <label class="field-label" for="qa-round">Round</label>
                <input id="qa-round"
                       class="field-input"
                       type="text"
                       [value]="getAssignmentRoundDisplay()"
                       readonly>
              </div>
            </div>

            <div class="modal-ft modal-ft--end">
              <div class="modal-actions">
                <button class="btn btn--ghost" (click)="closeAssignmentModal()">Cancel</button>
                <button class="btn btn--primary" (click)="saveAssignmentModal()">OK</button>
              </div>
            </div>
          </div>
        </div>
      }

      @if (isQuestionActionConfirmOpen && pendingQuestionAction) {
        <div class="modal-backdrop" (click)="cancelQuestionAction()">
          <div class="modal" (click)="$event.stopPropagation()">
            <div class="modal-hd">
              <h2 class="modal-title">Confirm Action</h2>
              <button class="modal-close" (click)="cancelQuestionAction()" aria-label="Close">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                     viewBox="0 0 24 24" fill="none" stroke="currentColor"
                     stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6"  x2="6"  y2="18"/>
                  <line x1="6"  y1="6"  x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div class="modal-body">
              <p class="confirm-text">{{ getQuestionActionConfirmationText(pendingQuestionAction) }}</p>
            </div>

            <div class="modal-ft modal-ft--end">
              <div class="modal-actions">
                <button class="btn btn--ghost" (click)="cancelQuestionAction()">Cancel</button>
                <button class="btn btn--primary" (click)="confirmQuestionAction()">Confirm</button>
              </div>
            </div>
          </div>
        </div>
      }

    </div>
  `,
  styles: [`
    /* ── Page shell ─────────────────────────────────────────── */
    .page { display: flex; flex-direction: column; min-height: 100vh; }

    /* ── Hero ───────────────────────────────────────────────── */
    .page-hero {
      background:
        radial-gradient(
          ellipse at 12% 60%,
          color-mix(in srgb, #6366f1 8%, transparent) 0%,
          transparent 55%
        ),
        linear-gradient(155deg, var(--color-bg-start) 0%, var(--color-bg-end) 100%);
      border-bottom: 1px solid var(--color-border);
      padding: 3rem 1.5rem 3.5rem;
    }

    .page-hero-inner {
      display: flex;
      flex-direction: column;
      gap: 1rem;
      margin: 0 auto;
      max-width: 1280px;
    }

    .home-btn {
      align-items: center;
      align-self: flex-start;
      background: color-mix(in srgb, var(--color-surface-2) 65%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.5rem;
      color: var(--color-muted);
      display: flex;
      font-size: 0.8rem;
      font-weight: 500;
      gap: 0.35rem;
      padding: 0.32rem 0.7rem;
      text-decoration: none;
      transition: background 0.15s, color 0.15s;
    }
    .home-btn:hover {
      background: color-mix(in srgb, var(--color-surface-2) 90%, transparent);
      color: var(--color-text);
    }

    .hero-top-row {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 0.85rem;
    }

    .hero-icon {
      align-items: center;
      border-radius: 0.75rem;
      display: flex;
      height: 52px;
      justify-content: center;
      width: 52px;
    }
    .hero-icon--indigo {
      background: color-mix(in srgb, #6366f1 16%, transparent);
      color: #818cf8;
    }

    .page-title {
      color: var(--color-text);
      font-size: clamp(1.6rem, 3vw, 2.2rem);
      font-weight: 800;
      line-height: 1.15;
      margin: 0;
    }

    .page-sub {
      color: var(--color-muted);
      font-size: 0.93rem;
      line-height: 1.6;
      margin: 0;
      max-width: 52ch;
    }

    /* ── Body ───────────────────────────────────────────────── */
    .page-body { flex: 1; padding: 2.5rem 1.5rem; }

    .page-body-inner {
      display: flex;
      flex-direction: column;
      gap: 1.25rem;
      margin: 0 auto;
      max-width: 1280px;
    }

    /* ── Questions card ─────────────────────────────────────── */
    .questions-card {
      background: color-mix(in srgb, var(--color-surface-2) 75%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 1rem;
      overflow: hidden;
    }

    .questions-card-hd {
      align-items: center;
      border-bottom: 1px solid var(--color-border);
      display: flex;
      justify-content: space-between;
      padding: 0.85rem 1.25rem;
    }

    .questions-card-title { color: var(--color-text); font-size: 0.88rem; font-weight: 700; }
    .questions-count      { color: var(--color-muted); font-size: 0.75rem; }
    .questions-list       { padding: 0.2rem 0; }

    .questions-col-hd {
      border-bottom: 1px solid var(--color-border);
      color: var(--color-muted);
      display: grid;
      font-size: 0.7rem;
      font-weight: 600;
      gap: 0.5rem;
      grid-template-columns: 2fr 1fr 1.1fr 0.8fr 28px;
      letter-spacing: 0.06em;
      padding: 0.45rem 1.25rem;
      text-transform: uppercase;
    }

    .question-row {
      align-items: center;
      border-bottom: 1px solid color-mix(in srgb, var(--color-border) 45%, transparent);
      cursor: pointer;
      display: grid;
      gap: 0.5rem;
      grid-template-columns: 2fr 1fr 1.1fr 0.8fr 28px;
      padding: 0.75rem 1.25rem;
      transition: background 0.15s;
    }
    .question-row:last-child { border-bottom: none; }
    .question-row:hover { background: color-mix(in srgb, #6366f1 4%, transparent); }

    .row--selected {
      background: color-mix(in srgb, #6366f1 8%, transparent);
      border-left: 3px solid #818cf8;
      padding-left: calc(1.25rem - 3px);
    }

    .question-text {
      color: var(--color-text);
      font-size: 0.88rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .question-event {
      color: var(--color-muted);
      font-size: 0.8rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .question-game-flow {
      align-items: center;
      color: var(--color-muted);
      display: inline-flex;
      font-size: 0.8rem;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .question-game-flow-icon {
      border-radius: 0.25rem;
      display: block;
      height: 3.0rem;
      width: 3.0rem;
    }

    .question-status {
      border: 1px solid color-mix(in srgb, #22c55e 55%, var(--color-border));
      border-radius: 9999px;
      color: #86efac;
      font-size: 0.68rem;
      font-weight: 700;
      justify-self: start;
      padding: 0.12rem 0.5rem;
      text-transform: uppercase;
    }

    .question-status--disabled {
      border-color: color-mix(in srgb, #f59e0b 55%, var(--color-border));
      color: #fbbf24;
    }

    .question-cell-edit {
      align-items: center;
      display: flex;
      gap: 0.5rem;
      min-width: 0;
    }

    .q-delete {
      align-items: center;
      background: transparent;
      border: none;
      border-radius: 0.35rem;
      color: var(--color-muted);
      cursor: pointer;
      display: flex;
      flex-shrink: 0;
      height: 26px;
      justify-content: center;
      opacity: 0.4;
      padding: 0;
      transition: background 0.15s, color 0.15s, opacity 0.15s;
      width: 26px;
    }
    .q-delete:hover {
      background: color-mix(in srgb, #ef4444 16%, transparent);
      color: #f87171;
      opacity: 1;
    }

    .row-chevron {
      align-items: center;
      background: transparent;
      border: none;
      border-radius: 0.35rem;
      color: var(--color-muted);
      cursor: pointer;
      display: flex;
      height: 26px;
      justify-content: center;
      opacity: 0.6;
      padding: 0;
      transition: background 0.15s, color 0.15s, opacity 0.15s;
      width: 26px;
    }
    .row-chevron:hover {
      background: color-mix(in srgb, #6366f1 18%, transparent);
      color: #c7d2fe;
      opacity: 1;
    }

    .questions-empty {
      color: var(--color-muted);
      font-size: 0.83rem;
      padding: 2.5rem;
      text-align: center;
    }

    .questions-hd-right {
      align-items: center;
      display: flex;
      gap: 0.85rem;
    }

    .questions-filter-wrap {
      align-items: center;
      color: var(--color-muted);
      display: flex;
      gap: 0.4rem;
    }

    .questions-filter-input {
      background: #1e293b;
      border: 1px solid #334155;
      border-radius: 0.4rem;
      color: #f8fafc;
      font-size: 0.75rem;
      outline: none;
      padding: 0.22rem 0.5rem;
      width: 14rem;
    }
    .questions-filter-input::placeholder { color: var(--color-muted); }
    .questions-filter-input:focus { border-color: #818cf8; }

    /* ── Action bar ─────────────────────────────────────────── */
    .action-bar { display: flex; gap: 0.75rem; padding-top: 0.25rem; }

    /* ── Buttons ────────────────────────────────────────────── */
    .btn {
      align-items: center;
      border: 1px solid transparent;
      border-radius: 0.5rem;
      cursor: pointer;
      display: inline-flex;
      font-size: 0.83rem;
      font-weight: 600;
      gap: 0.4rem;
      padding: 0.5rem 1rem;
      transition: background 0.15s, border-color 0.15s, color 0.15s;
    }
    .btn:disabled {
      cursor: not-allowed;
      opacity: 0.45;
    }
    .btn--primary { background: #6366f1; color: #fff; }
    .btn--primary:hover { background: color-mix(in srgb, #6366f1 82%, #fff); }
    .btn--ghost {
      background: color-mix(in srgb, var(--color-surface-2) 75%, transparent);
      border-color: var(--color-border);
      color: var(--color-muted);
    }
    .btn--ghost:hover { color: var(--color-text); }
    .btn--danger {
      background: color-mix(in srgb, #ef4444 16%, transparent);
      border-color: color-mix(in srgb, #ef4444 55%, var(--color-border));
      color: #fca5a5;
    }
    .btn--danger:hover {
      background: color-mix(in srgb, #ef4444 24%, transparent);
      color: #fee2e2;
    }

    .btn--lifecycle-blue {
      background: color-mix(in srgb, #38bdf8 20%, transparent);
      border-color: color-mix(in srgb, #38bdf8 35%, transparent);
      color: #7dd3fc;
    }
    .btn--lifecycle-blue:hover {
      background: color-mix(in srgb, #38bdf8 30%, transparent);
      border-color: color-mix(in srgb, #38bdf8 52%, transparent);
      color: #bae6fd;
    }
    .btn--lifecycle-blue-active {
      background: color-mix(in srgb, #38bdf8 42%, transparent);
      border-color: color-mix(in srgb, #38bdf8 70%, transparent);
      box-shadow: 0 0 0 1px color-mix(in srgb, #38bdf8 28%, transparent), 0 0 18px color-mix(in srgb, #38bdf8 40%, transparent);
      color: #e0f2fe;
    }
    .btn--danger-active {
      background: color-mix(in srgb, #ef4444 34%, transparent);
      border-color: color-mix(in srgb, #ef4444 62%, transparent);
      box-shadow: 0 0 0 1px color-mix(in srgb, #ef4444 26%, transparent), 0 0 18px color-mix(in srgb, #ef4444 38%, transparent);
      color: #fecaca;
    }

    /* ── Answers card ───────────────────────────────────────── */
    .answers-card {
      background: color-mix(in srgb, var(--color-surface-2) 75%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 1rem;
      overflow: hidden;
    }

    .answers-card-hd {
      border-bottom: 1px solid var(--color-border);
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
      padding: 0.85rem 1.25rem;
    }

    .answers-card-title { color: var(--color-text); font-size: 0.88rem; font-weight: 700; }

    .answers-sub {
      color: var(--color-muted);
      font-size: 0.78rem;
      line-height: 1.45;
      max-width: 80ch;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .answers-grid {
      display: grid;
      gap: 0.45rem 0.75rem;
      grid-auto-flow: column;
      grid-template-columns: 1fr 1fr;
      grid-template-rows: repeat(5, auto);
      padding: 1rem 1.25rem;
    }

    .answer-item {
      align-items: start;
      border-radius: 0.45rem;
      column-gap: 0.35rem;
      display: grid;
      grid-template-columns: auto auto minmax(0, 1fr) auto;
      padding: 0.1rem 0.15rem;
      transition: background 0.12s, outline 0.12s;
    }
    .answer-item.is-dragging { opacity: 0.3; }
    .answer-item.is-drag-over {
      background: color-mix(in srgb, #6366f1 12%, transparent);
      outline: 1px dashed #818cf8;
      outline-offset: 1px;
    }

    .answer-drag-handle {
      color: var(--color-muted);
      cursor: grab;
      display: flex;
      flex-shrink: 0;
      opacity: 0.3;
      transition: opacity 0.15s;
      user-select: none;
    }
    .answer-item:hover .answer-drag-handle { opacity: 0.85; }
    .answer-drag-handle:active { cursor: grabbing; }

    .answer-num {
      color: var(--color-muted);
      flex-shrink: 0;
      font-size: 0.68rem;
      font-weight: 600;
      min-width: 1rem;
      text-align: right;
      user-select: none;
    }

    .answer-main-wrap {
      display: flex;
      flex-direction: column;
      flex: 1;
      gap: 0.35rem;
      min-width: 0;
    }

    .answer-input {
      background: color-mix(in srgb, var(--color-surface-2) 60%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.45rem;
      box-sizing: border-box;
      color: var(--color-text);
      flex: 1;
      font-size: 0.83rem;
      outline: none;
      padding: 0.42rem 0.7rem;
      transition: border-color 0.15s;
      width: 100%;
    }
    .answer-input:focus { border-color: #818cf8; }
    .answer-input::placeholder { color: var(--color-muted); }

    .answer-hint-wrap {
      display: flex;
      flex-direction: column;
      gap: 0.22rem;
      margin-left: 0.8rem;
      min-width: 0;
    }

    .answer-hint-toggle {
      align-items: center;
      color: var(--color-muted);
      display: inline-flex;
      font-size: 0.7rem;
      font-weight: 600;
      gap: 0.32rem;
      letter-spacing: 0.03em;
      line-height: 1;
      text-transform: uppercase;
      user-select: none;
      width: fit-content;
    }

    .answer-hint-toggle input[type='checkbox'] {
      accent-color: #818cf8;
      margin: 0;
    }

    .answer-hint-label {
      color: var(--color-muted);
      font-size: 0.62rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      line-height: 1;
      text-transform: uppercase;
    }

    .answer-hint-input {
      background: color-mix(in srgb, var(--color-surface-2) 60%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.45rem;
      box-sizing: border-box;
      color: var(--color-text);
      font-family: inherit;
      font-size: 0.76rem;
      line-height: 1.35;
      min-height: 2.15rem;
      outline: none;
      padding: 0.36rem 0.55rem;
      resize: vertical;
      transition: border-color 0.15s;
      width: 100%;
    }
    .answer-hint-input:focus { border-color: #818cf8; }
    .answer-hint-input::placeholder { color: var(--color-muted); }

    .answer-points-input {
      background: color-mix(in srgb, var(--color-surface-2) 60%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.45rem;
      color: var(--color-text);
      flex-shrink: 0;
      font-size: 0.78rem;
      outline: none;
      padding: 0.42rem 0.5rem;
      text-align: right;
      transition: border-color 0.15s;
      width: 4.25rem;
    }
    .answer-points-input:focus { border-color: #818cf8; }
    .answer-points-input::-webkit-outer-spin-button,
    .answer-points-input::-webkit-inner-spin-button {
      -webkit-appearance: none;
      margin: 0;
    }
    .answer-points-input[type=number] {
      -moz-appearance: textfield;
      appearance: textfield;
    }

    .answer-points-wrap {
      align-items: flex-end;
      display: flex;
      flex-direction: column;
      gap: 0.2rem;
      justify-self: end;
    }

    .answer-points-validation {
      color: #fca5a5;
      font-size: 0.66rem;
      line-height: 1;
      white-space: nowrap;
    }

    /* ── Inline question editing ────────────────────────────── */
    .round-inline {
      align-items: center;
      background: color-mix(in srgb, #6366f1 16%, transparent);
      border-radius: 9999px;
      display: flex;
      flex-shrink: 0;
      gap: 0.3rem;
      padding: 0.1rem 0.5rem 0.1rem 0.6rem;
    }

    .round-inline-label {
      color: #818cf8;
      font-size: 0.64rem;
      font-weight: 700;
      letter-spacing: 0.04em;
      white-space: nowrap;
    }

    .round-edit {
      background: transparent;
      border: none;
      color: #818cf8;
      font-size: 0.64rem;
      font-weight: 700;
      outline: none;
      padding: 0;
      width: 2.4rem;
    }

    .question-edit-input {
      background: color-mix(in srgb, var(--color-surface-2) 60%, transparent);
      border: 1px solid #818cf8;
      border-radius: 0.4rem;
      box-sizing: border-box;
      color: var(--color-text);
      flex: 1;
      font-size: 0.88rem;
      outline: none;
      padding: 0.28rem 0.6rem;
    }

    .q-save {
      align-items: center;
      background: color-mix(in srgb, #22c55e 16%, transparent);
      border: none;
      border-radius: 0.35rem;
      color: #4ade80;
      cursor: pointer;
      display: flex;
      flex-shrink: 0;
      height: 26px;
      justify-content: center;
      padding: 0;
      transition: background 0.15s;
      width: 26px;
    }
    .q-save:hover { background: color-mix(in srgb, #22c55e 28%, transparent); }

    /* ── Modal ──────────────────────────────────────────────── */
    .modal-backdrop {
      align-items: center;
      background: rgba(0, 0, 0, 0.55);
      bottom: 0;
      display: flex;
      justify-content: center;
      left: 0;
      position: fixed;
      right: 0;
      top: 0;
      z-index: 1000;
    }

    .modal {
      background: var(--color-bg-end);
      border: 1px solid var(--color-border);
      border-radius: 1rem;
      box-shadow: 0 24px 64px rgba(0, 0, 0, 0.45);
      max-width: 520px;
      width: 100%;
    }

    .modal--wide {
      max-width: 980px;
    }

    .modal-hd {
      align-items: center;
      border-bottom: 1px solid var(--color-border);
      display: flex;
      justify-content: space-between;
      padding: 1.1rem 1.4rem;
    }

    .modal-title {
      color: var(--color-text);
      font-size: 1rem;
      font-weight: 700;
      margin: 0;
    }

    .modal-close {
      align-items: center;
      background: none;
      border: none;
      border-radius: 0.4rem;
      color: var(--color-muted);
      cursor: pointer;
      display: flex;
      padding: 0.25rem;
      transition: color 0.15s;
    }

    .modal-close:hover { color: var(--color-text); }

    .modal-body {
      display: flex;
      flex-direction: column;
      gap: 1.1rem;
      padding: 1.4rem;
    }

    .modal-section {
      display: flex;
      flex-direction: column;
      gap: 0.6rem;
    }

    .modal-ft {
      align-items: center;
      border-top: 1px solid var(--color-border);
      display: flex;
      justify-content: space-between;
      padding: 1rem 1.4rem;
    }

    .modal-ft--question-details {
      align-items: stretch;
      flex-direction: column;
      gap: 0.7rem;
    }

    .modal-ft-row {
      align-items: center;
      display: flex;
      flex-wrap: wrap;
      gap: 0.7rem;
      justify-content: space-between;
    }

    .modal-ft--end { justify-content: flex-end; }
    .modal-actions { display: flex; gap: 0.5rem; }

    .field { display: flex; flex-direction: column; gap: 0.4rem; }

    .field-label {
      color: var(--color-muted);
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.07em;
      text-transform: uppercase;
    }

    .field-textarea {
      background: color-mix(in srgb, var(--color-surface-2) 65%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.5rem;
      box-sizing: border-box;
      color: var(--color-text);
      font-family: inherit;
      font-size: 0.88rem;
      line-height: 1.5;
      padding: 0.6rem 0.75rem;
      resize: vertical;
      transition: border-color 0.15s;
      width: 100%;
    }

    .field-textarea:focus { border-color: #818cf8; outline: none; }
    .field-textarea::placeholder { color: var(--color-muted); }

    .gameflow-summary {
      background: color-mix(in srgb, var(--color-surface-2) 55%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.5rem;
      color: var(--color-muted);
      display: flex;
      flex-direction: column;
      font-size: 0.78rem;
      gap: 0.35rem;
      padding: 0.65rem 0.75rem;
    }

    .gameflow-summary p {
      margin: 0;
    }

    @media (max-width: 520px) {
      .answers-grid {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: repeat(5, auto);
      }
    }

    @media (max-width: 1040px) {
      .answer-main-wrap--with-hints {
        grid-template-columns: minmax(0, 1fr);
      }

      .answer-hint-wrap {
        margin-left: 0.55rem;
      }
    }

    /* ── Question Event Assignments card ────────────────────── */
    .assignments-card {
      background: color-mix(in srgb, var(--color-surface-2) 75%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 1rem;
      overflow: hidden;
    }

    .assignments-card-hd {
      align-items: center;
      border-bottom: 1px solid var(--color-border);
      display: flex;
      justify-content: space-between;
      padding: 0.85rem 1.25rem;
    }

    .assignments-card-title { color: var(--color-text); font-size: 0.88rem; font-weight: 700; }

    .assignments-hd-right {
      align-items: center;
      display: flex;
      gap: 0.85rem;
    }

    .filter-wrap {
      align-items: center;
      display: flex;
      gap: 0.4rem;
      color: var(--color-muted);
    }

    .filter-select {
      appearance: auto;
      background: #1e293b;
      border: 1px solid #334155;
      border-radius: 0.4rem;
      color: #f8fafc;
      font-size: 0.75rem;
      outline: none;
      padding: 0.22rem 0.5rem;
    }
    .filter-select:hover { border-color: #818cf8; }
    .filter-select:focus { border-color: #818cf8; outline: none; }
    .filter-select option { background: #0f172a; color: #f8fafc; }

    .assignments-count { color: var(--color-muted); font-size: 0.75rem; }

    .assignments-col-hd {
      border-bottom: 1px solid var(--color-border);
      color: var(--color-muted);
      display: grid;
      font-size: 0.7rem;
      font-weight: 600;
      gap: 0.5rem;
      grid-template-columns: 20px 2fr 1.5fr 70px 28px;
      letter-spacing: 0.06em;
      padding: 0.45rem 1.25rem;
      text-transform: uppercase;
    }

    .assignments-list { padding: 0.2rem 0; }

    .assignment-row {
      align-items: center;
      border-bottom: 1px solid color-mix(in srgb, var(--color-border) 40%, transparent);
      cursor: grab;
      display: grid;
      gap: 0.5rem;
      grid-template-columns: 20px 2fr 1.5fr 70px 28px;
      padding: 0.35rem 1.25rem;
      transition: background 0.12s, outline 0.12s;
    }
    .assignment-row:last-child { border-bottom: none; }
    .assignment-row.is-dragging { opacity: 0.35; }
    .assignment-row.is-drag-over {
      background: color-mix(in srgb, #6366f1 12%, transparent);
      outline: 1px dashed #818cf8;
      outline-offset: 1px;
    }
    .assignment-row.is-saving {
      cursor: progress;
    }

    .assignment-drag-handle {
      color: var(--color-muted);
      display: flex;
      justify-content: center;
      opacity: 0.35;
      user-select: none;
    }
    .assignment-row:hover .assignment-drag-handle {
      opacity: 0.9;
    }

    .asgn-select {
      background: color-mix(in srgb, var(--color-surface-2) 60%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.4rem;
      color: var(--color-text);
      font-size: 0.8rem;
      outline: none;
      padding: 0.3rem 0.5rem;
      width: 100%;
    }
    .asgn-select:focus { border-color: #818cf8; }
    .asgn-select option { background: #0f172a; color: #f8fafc; }

    .asgn-readonly {
      background: color-mix(in srgb, var(--color-surface-2) 60%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.4rem;
      color: var(--color-text);
      font-size: 0.8rem;
      overflow: hidden;
      padding: 0.36rem 0.5rem;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    .asgn-readonly--round {
      text-align: center;
    }

    .asgn-round {
      background: color-mix(in srgb, var(--color-surface-2) 60%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.4rem;
      color: var(--color-text);
      font-size: 0.8rem;
      outline: none;
      padding: 0.3rem 0.4rem;
      width: 100%;
    }
    .asgn-round:focus { border-color: #818cf8; }

    .asgn-delete {
      align-items: center;
      background: transparent;
      border: none;
      border-radius: 0.35rem;
      color: var(--color-muted);
      cursor: pointer;
      display: flex;
      height: 26px;
      justify-content: center;
      opacity: 0.4;
      padding: 0;
      transition: background 0.15s, color 0.15s, opacity 0.15s;
      width: 26px;
    }
    .asgn-delete:hover {
      background: color-mix(in srgb, #ef4444 16%, transparent);
      color: #f87171;
      opacity: 1;
    }

    .assignments-empty {
      color: var(--color-muted);
      font-size: 0.83rem;
      padding: 2.5rem;
      text-align: center;
    }

    .field-input,
    .field-select {
      background: color-mix(in srgb, var(--color-surface-2) 65%, transparent);
      border: 1px solid var(--color-border);
      border-radius: 0.5rem;
      box-sizing: border-box;
      color: var(--color-text);
      font-size: 0.88rem;
      padding: 0.52rem 0.75rem;
      transition: border-color 0.15s;
      width: 100%;
    }
    .field-input:focus,
    .field-select:focus {
      border-color: #818cf8;
      outline: none;
    }
    .confirm-text {
      color: var(--color-text);
      font-size: 0.88rem;
      line-height: 1.5;
      margin: 0;
    }

    .modal-toast {
      border: 1px solid color-mix(in srgb, #ef4444 38%, var(--color-border));
      border-radius: 0.5rem;
      display: flex;
      flex-direction: column;
      font-size: 0.8rem;
      gap: 0.22rem;
      line-height: 1.4;
      padding: 0.65rem 0.8rem;
    }

    .modal-toast-title {
      font-size: 0.72rem;
      font-weight: 700;
      letter-spacing: 0.05em;
      text-transform: uppercase;
    }

    .modal-toast--error {
      background: color-mix(in srgb, #ef4444 14%, transparent);
      color: #fecaca;
    }

    @media (max-width: 700px) {
      .assignments-col-hd,
      .assignment-row { grid-template-columns: 20px 1fr 70px 28px; }
      .assignments-col-hd .col-event,
      .assignment-row .asgn-readonly:first-of-type { display: none; }
    }
  `]
})
export class QuestionsPageComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly authService = inject(AuthService);
  private readonly organizationService = inject(OrganizationService);
  private readonly questionService = inject(QuestionService);
  private readonly questionAssignmentService = inject(QuestionAssignmentService);
  private readonly answerService = inject(AnswerService);
  private readonly answerHintService = inject(AnswerHintService);
  private readonly eventService = inject(EventService);

  private currentOrganizationId: number | null = null;
  private readonly minAnswerPoints = 0;
  private readonly maxAnswerPoints = 9999;

  readonly questions = signal<QuestionRecord[]>([]);
  readonly answers = signal<AnswerRecord[]>([]);
  readonly assignments = signal<QuestionAssignment[]>([]);

  eventOptions: EventOption[] = [];
  gameFlowModes: GameFlowModeDto[] = [];
  questionFilterEventId = 0;
  filterEventId = 0;
  filterText = '';

  get filteredAssignments(): QuestionAssignment[] {
    const filtered = this.filterEventId === 0
      ? this.assignments()
      : this.assignments().filter(a => a.eventId === this.filterEventId);

    return [...filtered].sort((a, b) => a.eventId - b.eventId || a.round - b.round || a.id - b.id);
  }

  get filteredQuestions(): QuestionRecord[] {
    let filtered = this.questions();

    if (this.questionFilterEventId !== 0) {
      filtered = filtered.filter(q =>
        this.assignments().some(a => a.questionId === q.id && a.eventId === this.questionFilterEventId)
      );
    }

    if (!this.filterText.trim()) {
      return filtered;
    }

    const lower = this.filterText.toLowerCase();
    return filtered.filter(q => q.text.toLowerCase().includes(lower));
  }

  selectedQuestion: QuestionRecord | null = null;
  editingQuestion: QuestionRecord | null = null;
  isQuestionDetailModalOpen = false;

  canManageQuestionLifecycle = false;
  canRestoreDeletedQuestions = false;
  canHardDeleteQuestions = false;

  get activeAnswers(): AnswerRecord[] {
    if (!this.selectedQuestion || this.selectedQuestion.isDeleted) return [];
    const answers = this.answers()
      .filter(a => a.questionId === this.selectedQuestion!.id)
      .sort((a, b) => a.order - b.order);

    if (this.isSingleAnswerGameFlowById(this.selectedQuestion.gameFlowId)) {
      return answers.slice(0, 1);
    }

    return answers;
  }

  get selectedQuestionSupportsHints(): boolean {
    if (!this.selectedQuestion) {
      return false;
    }

    return this.getGameFlowModeSupportsHints(this.selectedQuestion.gameFlowId);
  }

  isNewQuestionModalOpen = false;
  draft: QuestionDraft = { text: '', gameFlowId: null };
  isAssignmentModalOpen = false;
  assignmentDraft = { eventIds: [] as number[], round: 1 };
  isQuestionActionConfirmOpen = false;
  pendingQuestionAction: QuestionActionType | null = null;
  pendingQuestionActionQuestionId: number | null = null;
  pendingQuestionLifecycleAction: QuestionLifecycleAction = 'none';
  questionDetailToastMessage = '';
  dragSourceId: number | null = null;
  dragOverId: number | null = null;
  assignmentDragSourceId: number | null = null;
  assignmentDragOverId: number | null = null;
  isAssignmentReorderSaving = false;
  answerPointsValidationMessages: Record<number, string> = {};

  get canOpenAssignmentModal(): boolean {
    return !!this.selectedQuestion && !this.selectedQuestion.isDeleted && this.selectedQuestion.isEnabled;
  }

  get editingQuestionHasAssignments(): boolean {
    if (!this.editingQuestion) {
      return false;
    }

    return this.assignments().some(a => a.questionId === this.editingQuestion!.id);
  }

  get editingQuestionLifecycleState(): 'enabled' | 'disabled' | 'deleted' {
    if (!this.editingQuestion) {
      return 'enabled';
    }

    if (this.editingQuestion.isDeleted) {
      return 'deleted';
    }

    return this.editingQuestion.isEnabled ? 'enabled' : 'disabled';
  }

  get selectedQuestionAssignments(): QuestionAssignment[] {
    if (!this.selectedQuestion) {
      return [];
    }

    return this.assignments()
      .filter(a => a.questionId === this.selectedQuestion!.id)
      .sort((a, b) => a.round - b.round || a.id - b.id);
  }

  get hasSelectedQuestionAssignments(): boolean {
    return this.selectedQuestionAssignments.length > 0;
  }

  ngOnInit(): void {
    void this.initializeRoleContext();
    this.loadOrganizationContext();
    this.loadEvents();
    this.loadGameFlowModes();
    this.loadQuestions();
    this.loadAssignments();
  }

  private loadGameFlowModes(): void {
    this.questionService.getActiveGameFlowModes().subscribe({
      next: (modes) => {
        this.gameFlowModes = modes;
        const defaultMode = this.getDefaultGameFlowId();
        if (!this.draft.gameFlowId && defaultMode) {
          this.draft.gameFlowId = defaultMode;
        }
      },
      error: (err) => {
        console.error('Failed to load game flow modes:', err);
        this.gameFlowModes = [];
      }
    });
  }

  private async initializeRoleContext(): Promise<void> {
    const [isSiteAdmin, isOrgAdmin, isEventPlanner] = await Promise.all([
      this.authService.hasRoleAsync('SiteAdmin'),
      this.authService.hasRoleAsync('OrgAdmin'),
      this.authService.hasRoleAsync('EventPlanner')
    ]);

    window.setTimeout(() => {
      this.canManageQuestionLifecycle = isSiteAdmin || isOrgAdmin || isEventPlanner;
      this.canRestoreDeletedQuestions = isSiteAdmin;
      this.canHardDeleteQuestions = isSiteAdmin;
    }, 0);
  }

  private loadOrganizationContext(): void {
    this.organizationService.getMyOrganization().subscribe({
      next: (org) => {
        this.currentOrganizationId = org.id;
      },
      error: (err) => {
        console.error('Failed to load current organization:', err);
      }
    });
  }

  private mapAnswerDto(dto: AnswerDto): AnswerRecord {
    return {
      id: dto.id,
      questionId: dto.questionId,
      text: dto.answerText,
      order: dto.order,
      points: this.hasNonEmptyAnswerText(dto.answerText) ? (dto.points ?? 0) : 0,
      hintId: null,
      hintChecked: false,
      hintText: ''
    };
  }

  private loadAnswers(questionId: number): void {
    this.answerService.getByQuestionId(questionId).subscribe({
      next: (dtos) => {
        const mapped = dtos.map(d => this.mapAnswerDto(d));
        this.answers.update(list => [
          ...list.filter(a => a.questionId !== questionId),
          ...mapped
        ]);

        if (this.selectedQuestion && this.selectedQuestion.id === questionId && this.selectedQuestionSupportsHints) {
          this.loadAnswerHintsForQuestion(questionId, mapped);
        } else {
          this.clearAnswerHintsForQuestion(questionId);
        }
      },
      error: (err) => console.error('Failed to load answers:', err)
    });
  }

  private loadAnswerHintsForQuestion(questionId: number, answers: AnswerRecord[]): void {
    this.answerHintService.getByQuestionId(questionId).subscribe({
      next: (dtos: AnswerHintDto[]) => {
        const hintsByAnswerId = new Map(dtos.map(dto => [dto.answerId, dto]));
        const answerIds = new Set(answers.map(answer => answer.id));

        this.answers.update(list => list.map(item => {
          if (item.questionId !== questionId || !answerIds.has(item.id)) {
            return item;
          }

          const hint = hintsByAnswerId.get(item.id);
          if (!hint) {
            return { ...item, hintId: null, hintChecked: false, hintText: '' };
          }

          return {
            ...item,
            hintId: hint.id,
            hintChecked: !!hint.checked,
            hintText: hint.hintText ?? ''
          };
        }));
      },
      error: (err) => {
        console.error(`Failed to load answer hints for question ${questionId}:`, err);
        this.clearAnswerHintsForQuestion(questionId);
      }
    });
  }

  private clearAnswerHintsForQuestion(questionId: number, keepAnswerIds: number[] = []): void {
    const keep = new Set(keepAnswerIds);
    this.answers.update(list => list.map(answer => {
      if (answer.questionId !== questionId || keep.has(answer.id)) {
        return answer;
      }

      return { ...answer, hintId: null, hintChecked: false, hintText: '' };
    }));
  }

  private loadEvents(): void {
    this.eventService.getAll().subscribe({
      next: (events: EventDto[]) => {
        this.eventOptions = events.map(e => ({ id: e.id, name: e.name }));
        if (!this.currentOrganizationId && events.length > 0) {
          this.currentOrganizationId = events[0].organizationId;
        }
      },
      error: (err) => {
        console.error('Failed to load events:', err);
        this.eventOptions = [];
      }
    });
  }

  private loadQuestions(): void {
    this.questionService.getAll().subscribe({
      next: (dtos: QuestionDto[]) => {
        const mapped = dtos.map(d => ({
          id: d.id,
          organizationId: d.organizationId,
          gameFlowId: d.gameFlowId ?? 0,
          text: d.questionText,
          isEnabled: d.isEnabled ?? true,
          isDeleted: d.isDeleted ?? false
        }));
        this.questions.set(mapped);
      },
      error: (err) => {
        console.error('Failed to load questions:', err);
        this.questions.set([]);
      }
    });
  }

  private loadAssignments(): void {
    this.questionAssignmentService.getAll().subscribe({
      next: (dtos: QuestionAssignmentDto[]) => {
        this.assignments.set(dtos.map(d => ({ id: d.id, questionId: d.questionId, eventId: d.eventId, round: d.round })));
      },
      error: (err) => {
        console.error('Failed to load question assignments:', err);
        this.assignments.set([]);
      }
    });
  }

  getEventName(eventId: number): string {
    return this.eventOptions.find(e => e.id === eventId)?.name ?? '—';
  }

  getQuestionText(questionId: number): string {
    return this.questions().find(q => q.id === questionId)?.text ?? '—';
  }

  getQuestionEventNames(questionId: number): string {
    const eventIds = Array.from(new Set(
      this.assignments()
        .filter(a => a.questionId === questionId && a.eventId > 0)
        .map(a => a.eventId)
    ));

    if (eventIds.length === 0) {
      return '—';
    }

    const names = eventIds
      .map(eventId => this.getEventName(eventId))
      .filter(name => name !== '—');

    return names.length > 0 ? names.join(', ') : '—';
  }

  getGameFlowModeName(gameFlowId: number): string {
    return this.gameFlowModes.find(mode => mode.id === gameFlowId)?.name ?? '—';
  }

  private getGameFlowModeSupportsHints(gameFlowId: number): boolean {
    return this.gameFlowModes.find(mode => mode.id === gameFlowId)?.supportsHints ?? false;
  }

  selectQuestion(q: QuestionRecord): void {
    this.selectedQuestion = q;
    this.answerPointsValidationMessages = {};
    if (q.isDeleted) {
      this.answers.update(list => list.filter(a => a.questionId !== q.id));
      return;
    }
    this.loadAnswers(q.id);
  }

  openQuestionDetails(q: QuestionRecord): void {
    this.selectQuestion(q);
    this.editingQuestion = { ...q };
    this.pendingQuestionLifecycleAction = 'none';
    this.clearQuestionDetailToast();
    this.isQuestionDetailModalOpen = true;
  }

  closeQuestionDetailsModal(): void {
    this.isQuestionDetailModalOpen = false;
    this.editingQuestion = null;
    this.pendingQuestionLifecycleAction = 'none';
    this.clearQuestionDetailToast();
    this.dragSourceId = null;
    this.dragOverId = null;
  }

  selectQuestionLifecycleAction(action: QuestionLifecycleAction): void {
    if (!this.editingQuestion) {
      return;
    }

    if ((action === 'disable' || action === 'delete') && this.editingQuestionHasAssignments) {
      return;
    }

    if (action === 'disable' && (!this.editingQuestion.isEnabled || this.editingQuestion.isDeleted)) {
      return;
    }

    if (action === 'restore' && this.editingQuestion.isEnabled && !this.editingQuestion.isDeleted) {
      return;
    }

    if (action === 'delete' && this.editingQuestion.isDeleted) {
      return;
    }

    this.pendingQuestionLifecycleAction = this.pendingQuestionLifecycleAction === action ? 'none' : action;
  }

  isQuestionLifecycleActionSelected(action: QuestionLifecycleAction): boolean {
    return this.pendingQuestionLifecycleAction === action;
  }

  saveQuestionDetails(): void {
    if (!this.editingQuestion) {
      return;
    }

    this.clearQuestionDetailToast();

    const pendingAction = this.pendingQuestionLifecycleAction;
    if (pendingAction === 'delete') {
      const confirmed = window.confirm('Delete this question on OK confirmation? This performs a soft delete and disables the question.');
      if (!confirmed) {
        return;
      }
    }

    const questionText = this.editingQuestion.text.trim();
    if (!questionText && !this.editingQuestion.isDeleted) {
      return;
    }

    const gameFlowId = Number(this.editingQuestion.gameFlowId);
    if (!this.editingQuestion.isDeleted && (!Number.isFinite(gameFlowId) || gameFlowId <= 0)) {
      return;
    }

    if (!this.editingQuestion.isDeleted && this.isSingleAnswerGameFlowById(gameFlowId)) {
      const configuredAnswerCount = this.answers()
        .filter(a => a.questionId === this.editingQuestion!.id)
        .filter(a => this.hasNonEmptyAnswerText(a.text))
        .length;

      if (configuredAnswerCount !== 1) {
        this.setQuestionDetailToast(`Could not save this change. ${this.getGameFlowModeName(gameFlowId)} requires exactly 1 configured answer.`);
        return;
      }
    }

    const finalizeFlow = () => {
      this.executePendingQuestionLifecycleAction(() => {
        this.closeQuestionDetailsModal();
      });
    };

    if (this.editingQuestion.isDeleted) {
      finalizeFlow();
      return;
    }

    const dto: QuestionUpdateDto = {
      questionText,
      gameFlowId
    };
    this.questionService.update(this.editingQuestion.id, dto).subscribe({
      next: (updated) => {
        const mapped: QuestionRecord = {
          id: updated.id,
          organizationId: updated.organizationId,
          gameFlowId: updated.gameFlowId ?? gameFlowId,
          text: updated.questionText,
          isEnabled: updated.isEnabled ?? true,
          isDeleted: updated.isDeleted ?? false
        };
        this.questions.update(list => list.map(q => q.id === mapped.id ? mapped : q));
        this.selectedQuestion = mapped;
        this.editingQuestion = { ...mapped };
        finalizeFlow();
      },
      error: (err) => {
        this.handleQuestionDetailsSaveError(err, gameFlowId);
        console.error('Failed to update question:', err);
      }
    });
  }

  private handleQuestionDetailsSaveError(error: unknown, gameFlowId: number): void {
    const apiMessage = this.extractApiErrorMessage(error);
    const isSingleAnswerMode = this.isSingleAnswerGameFlowById(gameFlowId);
    const selectedModeName = this.getGameFlowModeName(gameFlowId);

    if ((error as { status?: number } | null)?.status === 400 && isSingleAnswerMode) {
      const detail = apiMessage ? ` ${apiMessage}` : '';
      this.setQuestionDetailToast(`Could not save this change. ${selectedModeName} supports only one answer. Keep one answer, then try again.${detail}`);
      return;
    }

    this.setQuestionDetailToast(apiMessage
      ? `Could not save question details. ${apiMessage}`
      : 'Could not save question details. Please review your changes and try again.');
  }

  private extractApiErrorMessage(error: unknown): string {
    if (!error || typeof error !== 'object') {
      return '';
    }

    const payload = error as {
      title?: string;
      detail?: string;
      errors?: Record<string, string[]>;
      message?: string;
      error?: unknown;
    };

    if (typeof payload.detail === 'string' && payload.detail.trim()) {
      return payload.detail.trim();
    }

    if (typeof payload.title === 'string' && payload.title.trim()) {
      return payload.title.trim();
    }

    if (payload.errors && typeof payload.errors === 'object') {
      const topLevelMessages = Object.values(payload.errors)
        .flat()
        .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
      if (topLevelMessages.length > 0) {
        return topLevelMessages.join(' ');
      }
    }

    if (typeof payload.error === 'string' && payload.error.trim()) {
      return payload.error.trim();
    }

    if (payload.error && typeof payload.error === 'object') {
      const errorBody = payload.error as {
        message?: string;
        title?: string;
        errors?: Record<string, string[]>;
      };

      if (typeof errorBody.message === 'string' && errorBody.message.trim()) {
        return errorBody.message.trim();
      }

      if (typeof errorBody.title === 'string' && errorBody.title.trim()) {
        return errorBody.title.trim();
      }

      if (errorBody.errors && typeof errorBody.errors === 'object') {
        const messages = Object.values(errorBody.errors)
          .flat()
          .filter((value): value is string => typeof value === 'string' && value.trim().length > 0);
        if (messages.length > 0) {
          return messages.join(' ');
        }
      }
    }

    if (typeof payload.message === 'string' && payload.message.trim()) {
      return payload.message.trim();
    }

    return '';
  }

  private clearQuestionDetailToast(): void {
    this.setQuestionDetailToast('');
  }

  private setQuestionDetailToast(message: string): void {
    this.questionDetailToastMessage = message;
    this.cdr.detectChanges();
  }

  private isSingleAnswerGameFlowById(gameFlowId: number): boolean {
    if (gameFlowId === PENALTY_SHOT_MODE_ID || gameFlowId === HEAD_TO_HEAD_MODE_ID) {
      return true;
    }

    const normalizedName = this.normalizeGameFlowName(this.getGameFlowModeName(gameFlowId));
    return normalizedName.includes('headtohead') || normalizedName.includes('penality') || normalizedName.includes('penalty');
  }

  private normalizeGameFlowName(value: string | null | undefined): string {
    return (value ?? '').replace(/[^a-z0-9]/gi, '').toLowerCase();
  }

  private executePendingQuestionLifecycleAction(onComplete: () => void): void {
    if (!this.editingQuestion || this.pendingQuestionLifecycleAction === 'none') {
      onComplete();
      return;
    }

    const questionId = this.editingQuestion.id;

    if (this.pendingQuestionLifecycleAction === 'disable') {
      this.questionService.setEnabled(questionId, false).subscribe({
        next: (dto) => {
          this.updateQuestionFromDto(dto);
          onComplete();
        },
        error: (err) => console.error('Failed to disable question:', err)
      });
      return;
    }

    if (this.pendingQuestionLifecycleAction === 'restore') {
      const restoreRequest = this.editingQuestion.isDeleted
        ? this.questionService.restore(questionId)
        : this.questionService.setEnabled(questionId, true);

      restoreRequest.subscribe({
        next: (dto) => {
          this.updateQuestionFromDto(dto);
          onComplete();
        },
        error: (err) => console.error('Failed to restore question:', err)
      });
      return;
    }

    this.questionService.delete(questionId).subscribe({
      next: () => {
        this.questions.update(list => list.map(q =>
          q.id === questionId
            ? { ...q, isDeleted: true, isEnabled: false }
            : q));
        this.answers.update(list => list.filter(a => a.questionId !== questionId));
        this.assignments.update(list => list.filter(a => a.questionId !== questionId));
        if (this.selectedQuestion?.id === questionId) {
          this.selectedQuestion = {
            ...this.selectedQuestion,
            isDeleted: true,
            isEnabled: false
          };
        }
        onComplete();
      },
      error: (err) => console.error('Failed to delete question:', err)
    });
  }

  deleteQuestion(questionId: number): void {
    this.requestQuestionAction('softDelete', questionId);
  }

  openAssignmentModal(): void {
    if (!this.canOpenAssignmentModal) {
      return;
    }

    this.assignmentDraft = { eventIds: [], round: 1 };
    this.recalculateAssignmentDraftRound();
    this.isAssignmentModalOpen = true;
  }

  closeAssignmentModal(): void {
    this.isAssignmentModalOpen = false;
  }

  onAssignmentEventSelectionChanged(selection: number[] | number): void {
    const values = Array.isArray(selection) ? selection.map(Number) : [Number(selection)];
    this.assignmentDraft.eventIds = [...new Set(values.filter(id => Number.isFinite(id) && id > 0))];
    this.recalculateAssignmentDraftRound();
  }

  getAssignmentRoundDisplay(): string {
    const selectedEventIds = [...new Set(this.assignmentDraft.eventIds.filter(id => id > 0))];
    if (selectedEventIds.length === 0) {
      return 'Select event(s)';
    }

    const rounds = selectedEventIds.map(eventId => this.getNextRoundForEvent(eventId));
    const firstRound = rounds[0];

    if (rounds.every(round => round === firstRound)) {
      return String(firstRound);
    }

    return 'Auto per event';
  }

  private recalculateAssignmentDraftRound(): void {
    const selectedEventIds = [...new Set(this.assignmentDraft.eventIds.filter(id => id > 0))];
    if (selectedEventIds.length === 0) {
      this.assignmentDraft.round = 1;
      return;
    }

    this.assignmentDraft.round = this.getNextRoundForEvent(selectedEventIds[0]);
  }

  private getNextRoundForEvent(eventId: number): number {
    const maxRound = this.assignments()
      .filter(a => a.eventId === eventId)
      .reduce((max, assignment) => Math.max(max, assignment.round), 0);

    return maxRound + 1;
  }

  saveAssignmentModal(): void {
    if (!this.selectedQuestion || this.selectedQuestion.isDeleted) {
      return;
    }

    const selectedEventIds = [...new Set(this.assignmentDraft.eventIds.filter(id => id > 0))];
    if (selectedEventIds.length === 0) {
      return;
    }

    const eventIdsToCreate = selectedEventIds.filter(eventId =>
      !this.assignments().some(a => a.eventId === eventId && a.questionId === this.selectedQuestion!.id)
    );

    if (eventIdsToCreate.length === 0) {
      this.closeAssignmentModal();
      return;
    }

    const roundsByEventId = new Map<number, number>(
      eventIdsToCreate.map(eventId => [eventId, this.getNextRoundForEvent(eventId)])
    );

    this.createAssignmentsSequentially(this.selectedQuestion.id, eventIdsToCreate, roundsByEventId, 0);
  }

  private createAssignmentsSequentially(questionId: number, eventIds: number[], roundsByEventId: Map<number, number>, index: number): void {
    if (index >= eventIds.length) {
      this.loadAssignments();
      this.closeAssignmentModal();
      return;
    }

    const eventId = eventIds[index];
    const round = roundsByEventId.get(eventId);
    if (!round || round < 1) {
      this.createAssignmentsSequentially(questionId, eventIds, roundsByEventId, index + 1);
      return;
    }

    const dto: QuestionAssignmentCreateDto = {
      questionId,
      eventId,
      round
    };

    this.questionAssignmentService.create(dto).subscribe({
      next: () => {
        this.createAssignmentsSequentially(questionId, eventIds, roundsByEventId, index + 1);
      },
      error: (err) => console.error('Failed to create assignment:', err)
    });
  }

  private executeSoftDeleteQuestion(questionId: number): void {

    this.questionService.delete(questionId).subscribe({
      next: () => {
        this.questions.update(list => list.map(q =>
          q.id === questionId
            ? { ...q, isDeleted: true, isEnabled: false }
            : q));
        this.answers.update(list => list.filter(a => a.questionId !== questionId));
        this.assignments.update(list => list.filter(a => a.questionId !== questionId));
        if (this.selectedQuestion?.id === questionId) {
          this.selectedQuestion = {
            ...this.selectedQuestion,
            isDeleted: true,
            isEnabled: false
          };
          this.editingQuestion = null;
        }
      },
      error: (err) => console.error('Failed to delete question:', err)
    });
  }

  openNew(): void {
    this.draft = {
      text: '',
      gameFlowId: this.getDefaultGameFlowId()
    };
    this.isNewQuestionModalOpen = true;
  }

  closeNewQuestionModal(): void {
    this.isNewQuestionModalOpen = false;
  }

  saveQuestion(): void {
    if (!this.draft.text.trim()) return;

    const gameFlowId = Number(this.draft.gameFlowId);
    if (!Number.isFinite(gameFlowId) || gameFlowId <= 0) {
      return;
    }

    const resolvedOrganizationId = this.currentOrganizationId;
    if (!resolvedOrganizationId || resolvedOrganizationId <= 0) {
      console.error('Organization context is missing for question creation.');
      return;
    }

    const dto: QuestionCreateDto = {
      organizationId: resolvedOrganizationId,
      questionText: this.draft.text.trim(),
      gameFlowId
    };

    this.questionService.create(dto).subscribe({
      next: (created) => {
        const newQ: QuestionRecord = {
          id: created.id,
          organizationId: created.organizationId,
          gameFlowId: created.gameFlowId ?? gameFlowId,
          text: created.questionText,
          isEnabled: created.isEnabled ?? true,
          isDeleted: created.isDeleted ?? false
        };

        this.questionFilterEventId = 0;
        this.filterText = '';

        this.questions.update(list => [...list, newQ]);
        this.selectedQuestion = newQ;
        this.editingQuestion = null;
        this.loadAnswers(newQ.id);
        this.closeNewQuestionModal();
      },
      error: (err) => console.error('Failed to create question:', err)
    });
  }

  saveAnswer(answer: AnswerRecord): void {
    if (!this.hasAnswerText(answer)) {
      answer.points = 0;
      delete this.answerPointsValidationMessages[answer.id];
    }

    const { value } = this.normalizeAnswerPoints(answer.points);
    answer.points = value;

    const dto: AnswerUpdateDto = {
      answerText: answer.text,
      order: answer.order,
      points: answer.points,
      topic: undefined
    };

    this.answerService.update(answer.id, dto).subscribe({
      error: (err) => console.error('Failed to update answer:', err)
    });
  }

  onAnswerPointsChanged(answer: AnswerRecord, value: number | string): void {
    const { value: normalized, outOfRange } = this.normalizeAnswerPoints(value);
    answer.points = normalized;

    if (outOfRange) {
      this.answerPointsValidationMessages[answer.id] = `Points must be between ${this.minAnswerPoints} and ${this.maxAnswerPoints}.`;
      return;
    }

    delete this.answerPointsValidationMessages[answer.id];
  }

  onAnswerTextChanged(answer: AnswerRecord, value: string | null): void {
    answer.text = value ?? '';

    if (!this.hasAnswerText(answer)) {
      answer.points = 0;
      delete this.answerPointsValidationMessages[answer.id];
      answer.hintChecked = false;
      answer.hintText = '';
    }
  }

  onAnswerHintChanged(answer: AnswerRecord, value: string | null): void {
    answer.hintText = (value ?? '').slice(0, 500);
  }

  onAnswerHintCheckedChanged(answer: AnswerRecord, value: boolean): void {
    answer.hintChecked = !!value;
    if (!answer.hintChecked) {
      answer.hintText = '';
    }

    this.saveAnswerHint(answer);
  }

  onAnswerHintEnter(answer: AnswerRecord, event: Event): void {
    if (!answer.hintText.trim()) {
      event.preventDefault();
      this.saveAnswerHint(answer);
    }
  }

  saveAnswerHint(answer: AnswerRecord): void {
    if (!this.selectedQuestionSupportsHints) {
      return;
    }

    const normalizedHintText = answer.hintText.trim();
    const payloadHint = answer.hintChecked && normalizedHintText.length > 0 ? normalizedHintText : null;

    if (answer.hintId) {
      this.answerHintService.update(answer.hintId, { checked: answer.hintChecked, hintText: payloadHint ?? undefined }).subscribe({
        next: (dto) => {
          this.answers.update(list => list.map(item =>
            item.id === answer.id
              ? { ...item, hintId: dto.id, hintChecked: !!dto.checked, hintText: dto.hintText ?? '' }
              : item));
        },
        error: (err) => console.error('Failed to update answer hint:', err)
      });
      return;
    }

    if (!answer.hintChecked && !payloadHint) {
      return;
    }

    this.answerHintService.create({ answerId: answer.id, checked: answer.hintChecked, hintText: payloadHint ?? undefined }).subscribe({
      next: (dto) => {
        this.answers.update(list => list.map(item =>
          item.id === answer.id
            ? { ...item, hintId: dto.id, hintChecked: !!dto.checked, hintText: dto.hintText ?? '' }
            : item));
      },
      error: (err) => console.error('Failed to create answer hint:', err)
    });
  }

  hasAnswerText(answer: AnswerRecord): boolean {
    return this.hasNonEmptyAnswerText(answer.text);
  }

  private hasNonEmptyAnswerText(value: string | null | undefined): boolean {
    return !!value?.trim();
  }

  getAnswerPointsValidationMessage(answerId: number): string {
    return this.answerPointsValidationMessages[answerId] ?? '';
  }

  private normalizeAnswerPoints(value: number | string): { value: number; outOfRange: boolean } {
    const parsed = Number(value);
    if (!Number.isFinite(parsed)) {
      return { value: this.minAnswerPoints, outOfRange: false };
    }

    const integerValue = Math.trunc(parsed);
    const clamped = Math.min(this.maxAnswerPoints, Math.max(this.minAnswerPoints, integerValue));
    return { value: clamped, outOfRange: integerValue !== clamped };
  }

  deleteAssignment(assignmentId: number): void {
    this.questionAssignmentService.delete(assignmentId).subscribe({
      next: () => this.loadAssignments(),
      error: (err) => console.error('Failed to delete assignment:', err)
    });
  }

  onAssignmentDragStart(asgn: QuestionAssignment, event: DragEvent): void {
    if (this.isAssignmentReorderSaving) {
      event.preventDefault();
      return;
    }

    this.assignmentDragSourceId = asgn.id;
    event.dataTransfer!.effectAllowed = 'move';
  }

  onAssignmentDragOver(target: QuestionAssignment, event: DragEvent): void {
    if (!this.canDropAssignmentOnTarget(target)) {
      return;
    }

    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
    this.assignmentDragOverId = target.id;
  }

  onAssignmentDragLeave(event: DragEvent): void {
    const rel = event.relatedTarget as Node | null;
    if (!rel || !(event.currentTarget as Element).contains(rel)) {
      this.assignmentDragOverId = null;
    }
  }

  onAssignmentDrop(target: QuestionAssignment, event: DragEvent): void {
    event.preventDefault();
    this.assignmentDragOverId = null;

    if (!this.assignmentDragSourceId || this.assignmentDragSourceId === target.id) {
      this.assignmentDragSourceId = null;
      return;
    }

    const source = this.assignments().find(a => a.id === this.assignmentDragSourceId);
    if (!source || source.eventId !== target.eventId) {
      this.assignmentDragSourceId = null;
      return;
    }

    this.reorderAssignmentsWithinEvent(source.id, target.id, target.eventId);
  }

  onAssignmentDragEnd(): void {
    this.assignmentDragSourceId = null;
    this.assignmentDragOverId = null;
  }

  private canDropAssignmentOnTarget(target: QuestionAssignment): boolean {
    if (this.isAssignmentReorderSaving || !this.assignmentDragSourceId || this.assignmentDragSourceId === target.id) {
      return false;
    }

    const source = this.assignments().find(a => a.id === this.assignmentDragSourceId);
    return !!source && source.eventId === target.eventId;
  }

  private reorderAssignmentsWithinEvent(sourceAssignmentId: number, targetAssignmentId: number, eventId: number): void {
    const eventAssignments = this.assignments()
      .filter(a => a.eventId === eventId)
      .sort((a, b) => a.round - b.round || a.id - b.id);

    const sourceIndex = eventAssignments.findIndex(a => a.id === sourceAssignmentId);
    const targetIndex = eventAssignments.findIndex(a => a.id === targetAssignmentId);
    if (sourceIndex < 0 || targetIndex < 0) {
      this.assignmentDragSourceId = null;
      return;
    }

    const reordered = [...eventAssignments];
    const [moved] = reordered.splice(sourceIndex, 1);
    reordered.splice(targetIndex, 0, moved);

    const roundByAssignmentId = new Map<number, number>(reordered.map((assignment, index) => [assignment.id, index + 1]));
    const changedAssignments = reordered
      .map(assignment => {
        const nextRound = roundByAssignmentId.get(assignment.id)!;
        return assignment.round === nextRound ? null : { ...assignment, round: nextRound };
      })
      .filter((assignment): assignment is QuestionAssignment => assignment !== null);

    if (changedAssignments.length === 0) {
      this.assignmentDragSourceId = null;
      return;
    }

    this.assignments.update(list =>
      list.map(assignment => {
        const nextRound = roundByAssignmentId.get(assignment.id);
        return nextRound ? { ...assignment, round: nextRound } : assignment;
      })
    );

    this.isAssignmentReorderSaving = true;
    this.persistAssignmentRoundUpdates(changedAssignments, 0);
  }

  private persistAssignmentRoundUpdates(changedAssignments: QuestionAssignment[], index: number): void {
    if (index >= changedAssignments.length) {
      this.isAssignmentReorderSaving = false;
      this.assignmentDragSourceId = null;
      this.assignmentDragOverId = null;
      this.loadAssignments();
      return;
    }

    const assignment = changedAssignments[index];
    this.questionAssignmentService.update(assignment.id, {
      questionId: assignment.questionId,
      eventId: assignment.eventId,
      round: assignment.round
    }).subscribe({
      next: () => this.persistAssignmentRoundUpdates(changedAssignments, index + 1),
      error: (err) => {
        console.error('Failed to reorder question assignments:', err);
        this.isAssignmentReorderSaving = false;
        this.assignmentDragSourceId = null;
        this.assignmentDragOverId = null;
        this.loadAssignments();
      }
    });
  }

  onDragStart(ans: AnswerRecord, event: DragEvent): void {
    this.dragSourceId = ans.id;
    event.dataTransfer!.effectAllowed = 'move';
  }

  onDragOver(ans: AnswerRecord, event: DragEvent): void {
    event.preventDefault();
    event.dataTransfer!.dropEffect = 'move';
    this.dragOverId = ans.id;
  }

  onDragLeave(event: DragEvent): void {
    const rel = event.relatedTarget as Node | null;
    if (!rel || !(event.currentTarget as Element).contains(rel)) {
      this.dragOverId = null;
    }
  }

  onDrop(targetAns: AnswerRecord, event: DragEvent): void {
    event.preventDefault();
    this.dragOverId = null;
    if (this.dragSourceId === null || this.dragSourceId === targetAns.id) {
      this.dragSourceId = null;
      return;
    }
    const current = [...this.activeAnswers];
    const srcIdx = current.findIndex(a => a.id === this.dragSourceId);
    const tgtIdx = current.findIndex(a => a.id === targetAns.id);
    if (srcIdx === -1 || tgtIdx === -1) { this.dragSourceId = null; return; }

    const [moved] = current.splice(srcIdx, 1);
    current.splice(tgtIdx, 0, moved);

    const orderMap = new Map(current.map((a, i) => [a.id, i + 1]));
    this.answers.update(list =>
      list.map(a => orderMap.has(a.id) ? { ...a, order: orderMap.get(a.id)! } : a)
    );

    current.forEach(a => this.saveAnswer({ ...a, order: orderMap.get(a.id)! }));
    this.dragSourceId = null;
  }

  onDragEnd(): void {
    this.dragSourceId = null;
    this.dragOverId = null;
  }

  toggleQuestionEnabled(): void {
    if (!this.selectedQuestion || this.selectedQuestion.isDeleted) {
      return;
    }

    this.requestQuestionAction(this.selectedQuestion.isEnabled ? 'disable' : 'enable', this.selectedQuestion.id);
  }

  restoreQuestion(): void {
    if (!this.selectedQuestion || !this.selectedQuestion.isDeleted || !this.canRestoreDeletedQuestions) {
      return;
    }

    this.requestQuestionAction('restore', this.selectedQuestion.id);
  }

  hardDeleteQuestion(): void {
    if (!this.selectedQuestion || !this.selectedQuestion.isDeleted || !this.canHardDeleteQuestions) {
      return;
    }

    this.requestQuestionAction('hardDelete', this.selectedQuestion.id);
  }

  private requestQuestionAction(action: QuestionActionType, questionId?: number): void {
    const resolvedQuestionId = questionId ?? this.selectedQuestion?.id ?? null;
    if (!resolvedQuestionId) {
      return;
    }

    this.pendingQuestionAction = action;
    this.pendingQuestionActionQuestionId = resolvedQuestionId;
    this.isQuestionActionConfirmOpen = true;
  }

  cancelQuestionAction(): void {
    this.isQuestionActionConfirmOpen = false;
    this.pendingQuestionAction = null;
    this.pendingQuestionActionQuestionId = null;
  }

  confirmQuestionAction(): void {
    if (!this.pendingQuestionAction || !this.pendingQuestionActionQuestionId) {
      return;
    }

    const action = this.pendingQuestionAction;
    const questionId = this.pendingQuestionActionQuestionId;
    this.cancelQuestionAction();

    if (action === 'softDelete') {
      this.executeSoftDeleteQuestion(questionId);
      return;
    }

    if (action === 'hardDelete') {
      this.executeHardDeleteQuestion(questionId);
      return;
    }

    if (action === 'restore') {
      this.executeRestoreQuestion(questionId);
      return;
    }

    this.executeToggleQuestionEnabled(questionId);
  }

  getQuestionActionConfirmationText(action: QuestionActionType): string {
    if (action === 'disable') {
      return 'Are you sure you want to disable this question?';
    }

    if (action === 'enable') {
      return 'Are you sure you want to enable this question?';
    }

    if (action === 'softDelete') {
      return 'Are you sure you want to soft delete this question?';
    }

    if (action === 'restore') {
      return 'Are you sure you want to restore this question?';
    }

    return 'Are you sure you want to permanently delete this question?';
  }

  private executeToggleQuestionEnabled(questionId: number): void {
    const existing = this.questions().find(q => q.id === questionId);
    if (!existing || existing.isDeleted) {
      return;
    }

    this.questionService.setEnabled(questionId, !existing.isEnabled).subscribe({
      next: (dto) => this.updateQuestionFromDto(dto),
      error: (err) => console.error('Failed to toggle question enabled state:', err)
    });
  }

  private executeRestoreQuestion(questionId: number): void {
    const existing = this.questions().find(q => q.id === questionId);
    if (!existing || !existing.isDeleted) {
      return;
    }

    this.questionService.restore(questionId).subscribe({
      next: (dto) => this.updateQuestionFromDto(dto),
      error: (err) => console.error('Failed to restore question:', err)
    });
  }

  private executeHardDeleteQuestion(questionId: number): void {
    const existing = this.questions().find(q => q.id === questionId);
    if (!existing || !existing.isDeleted) {
      return;
    }

    this.questionService.hardDelete(questionId).subscribe({
      next: () => {
        this.questions.update(list => list.filter(q => q.id !== questionId));
        this.answers.update(list => list.filter(a => a.questionId !== questionId));
        this.assignments.update(list => list.filter(a => a.questionId !== questionId));
        this.selectedQuestion = null;
        this.editingQuestion = null;
      },
      error: (err) => console.error('Failed to hard delete question:', err)
    });
  }

  private updateQuestionFromDto(dto: QuestionDto): void {
    const updated: QuestionRecord = {
      id: dto.id,
      organizationId: dto.organizationId,
      gameFlowId: dto.gameFlowId ?? 0,
      text: dto.questionText,
      isEnabled: dto.isEnabled ?? true,
      isDeleted: dto.isDeleted ?? false
    };

    this.questions.update(list => list.map(q => q.id === updated.id ? updated : q));
    this.selectedQuestion = this.selectedQuestion?.id === updated.id ? updated : this.selectedQuestion;
    this.editingQuestion = this.editingQuestion?.id === updated.id ? { ...updated } : this.editingQuestion;

    if (updated.isDeleted) {
      this.answers.update(list => list.filter(a => a.questionId !== updated.id));
      this.assignments.update(list => list.filter(a => a.questionId !== updated.id));
    } else if (this.selectedQuestion?.id === updated.id) {
      this.loadAnswers(updated.id);
    }
  }

  private getDefaultGameFlowId(): number | null {
    if (this.gameFlowModes.length === 0) {
      return null;
    }

    return this.gameFlowModes.find(mode => mode.defaultMode)?.id ?? this.gameFlowModes[0].id;
  }
}
